import { Link, defaultStringifySearch, useRouter, useSearch } from '@tanstack/react-router';
import { RotateCw, UserPlus } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { USER_LIST_STATUSES, type BulkUsersResponse, type UserListStatus } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import { Button } from '../../../../components/Button';
import { DataTable } from '../../../../components/data-table/DataTable';
import type { Column, SortSpec } from '../../../../components/data-table/types';
import { useTablePreferences } from '../../../../components/data-table/use-table-preferences';
import { SearchInput } from '../../../../components/Input';
import { Pager } from '../../../../components/Pager';
import { Select } from '../../../../components/Select';
import { EmptyState, FilteredEmptyState, ForbiddenState, ServerErrorState } from '../../../../components/system-states/SystemState';
import { ToneChip, toneClass } from '../../../../components/Tone';
import { EMPTY, formatDateTime, formatNumber } from '../../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../../lib/selection';
import { useMediaQuery } from '../../../../lib/use-media-query';
import { useCanEdit, useCanView } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { useRoles } from '../../api/roles-api';
import { useResendInvite, useUserCounts, useUsers, useVentures, type UserListRow } from '../../api/users-api';
import { InvitePane } from './InvitePane';
import type { PaneMode } from './PaneFrame';
import {
  PEOPLE_PAGE_SIZE,
  countsFilterOf,
  filterCount,
  filterKey,
  filterOf,
  listParamsOf,
  readPeopleSearch,
  sortOf,
  sortParamOf,
  type PeopleSearch,
} from './people-address';
import { PEOPLE_STATUS_LABELS, PEOPLE_STATUS_TONES, listStatusOf, reachOf } from './people-format';
import { PeopleBulkActions } from './PeopleBulkActions';
import { PersonPane, type PaneNavigation } from './PersonPane';
import { ResultList, bulkLines, bulkTotals } from './ResultList';
import fit from '../../../../components/FitPage.module.css';
import styles from './PeoplePage.module.css';

/**
 * USER-01, People (Admin redesign 2026-11-02, Option A "Master and detail"): everyone who can sign in, with the
 * person you click open in a pane beside the list, so an Admin can walk down the list with the arrow keys and fix
 * each person without leaving it.
 *
 * - Status tiles count and filter (All, Active, Invited, Locked, Suspended, Deactivated).
 * - Search, Role, Venture, Panel and Two-factor filters, the sort, the page and the open person are all in the
 *   address (06-qol §3), so a copied link opens the same view.
 * - Ticked people get the bulk actions; "all N matching" sends the filter, not a list of ids.
 * - The pane sits beside the list on a computer, over it from the laptop width down, and is its own screen on a
 *   phone (FRAME-SPEC §5). Invite opens in the same place.
 *
 * `/admin/users/$userId` opens this page with that person in the pane, and `/admin/invite` with the invite form.
 */

/** The breakpoints of semantic.css: the pane leaves the list's side at the laptop width, and takes the screen on a phone. */
const OVERLAY_MEDIA = '(max-width: 1180px)';
const PHONE_MEDIA = '(max-width: 640px)';
/** 06-qol §6: type-ahead waits this long after the last key. */
const SEARCH_DEBOUNCE_MS = 250;
const SCREEN_ID = 'user-list';
/** Two columns some Admins want, hidden until they ask for them in the columns menu. */
const HIDDEN_AT_FIRST = ['isPanelEligible', 'isTwoFactorEnabled'];

const ANY = '';

export function PeoplePage({
  routePersonId,
  isInviteRoute = false,
}: {
  /** `/admin/users/$userId`: this person is open in the pane. */
  readonly routePersonId?: string;
  /** `/admin/invite`: the invite form is open in the pane. */
  readonly isInviteRoute?: boolean;
}): ReactNode {
  const canView = useCanView('user_management');
  const canEdit = useCanEdit('user_management');
  const router = useRouter();
  const showToast = useToasts((state) => state.show);
  const raw = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const address = readPeopleSearch(raw);
  const search: PeopleSearch = {
    ...address,
    ...(address.person === undefined && routePersonId !== undefined ? { person: routePersonId } : {}),
    ...(isInviteRoute ? { invite: true } : {}),
  };

  // The toolbar's choices show their value; a hidden name says which filter each one is.
  const filterIds = { role: useId(), venture: useId(), panel: useId(), twoFactor: useId() };

  const isOverlay = useMediaQuery(OVERLAY_MEDIA);
  const isPhone = useMediaQuery(PHONE_MEDIA);
  const paneMode: PaneMode = isPhone ? 'page' : isOverlay ? 'overlay' : 'docked';

  const users = useUsers(listParamsOf(search), canView);
  const counts = useUserCounts(countsFilterOf(search), canView);
  // Review R29: the invite pane's "N waiting" is everybody invited, whatever the list is filtered to.
  const allCounts = useUserCounts({}, canView && search.invite === true);
  // Review R22: only for someone who may open People, or HR's visit asks for /roles and is refused.
  const roles = useRoles(canView);
  const ventures = useVentures();
  const resend = useResendInvite();

  // A selection belongs to the filter it was made under: a new filter starts with nothing ticked.
  const key = filterKey(search);
  const [selectionState, setSelectionState] = useState<{ readonly key: string; readonly selection: SelectionState }>({
    key,
    selection: EMPTY_SELECTION,
  });
  const selection = selectionState.key === key ? selectionState.selection : EMPTY_SELECTION;
  const setSelection = (next: SelectionState): void => setSelectionState({ key, selection: next });

  const [bulkResult, setBulkResult] = useState<{ readonly title: string; readonly response: BulkUsersResponse } | null>(null);

  /** Every change of view goes through the address, replacing it: Back leaves People rather than undoing filters. */
  const go = (next: PeopleSearch): void => {
    const { invite, ...rest } = next;
    router.history.replace(`/admin/users${defaultStringifySearch({ ...rest, ...(invite ? { invite: true } : {}) })}`);
  };
  /** A filter change goes back to the first page. */
  const filterBy = (next: Partial<PeopleSearch>): void => go({ ...search, ...next, page: undefined });

  // The search box answers each key at once; the list follows a moment after the typing stops.
  const [typed, setTyped] = useState<string | null>(null);
  const typingTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(typingTimer.current), []);
  const onType = (value: string): void => {
    setTyped(value);
    window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => filterBy({ q: value.trim() === '' ? undefined : value.trim() }), SEARCH_DEBOUNCE_MS);
  };

  const rows = users.data?.rows ?? [];
  const total = users.data?.total;
  const page = search.page ?? 1;
  const pageCount = Math.max(1, Math.ceil((total ?? 0) / PEOPLE_PAGE_SIZE));
  const ids = rows.map((row) => row.id);

  // Next on the last row of a page opens the first row of the next page (Previous, the last of the one before)
  // as soon as that page has arrived; until then the pane keeps the person it had.
  const [edge, setEdge] = useState<{ readonly page: number; readonly which: 'first' | 'last'; readonly from: string } | null>(null);
  const isPageReady = users.data !== undefined && users.data.page === page && !users.isPlaceholderData;

  // Review R29: a bulk action can empty the page you are on (everyone on it deactivated under a status filter). Step
  // back to the last page that has people, rather than leaving an empty table with no pager to get out.
  const lastPage = total === undefined ? null : Math.max(1, Math.ceil(total / PEOPLE_PAGE_SIZE));
  const isPastTheEnd = isPageReady && rows.length === 0 && page > 1 && lastPage !== null && lastPage < page;
  const backToLastPage =
    isPastTheEnd && lastPage !== null
      ? `/admin/users${defaultStringifySearch({ ...stripInvite(search), page: lastPage > 1 ? lastPage : undefined })}`
      : null;
  useEffect(() => {
    if (backToLastPage !== null) router.history.replace(backToLastPage);
  }, [backToLastPage, router]);
  const edgePerson =
    search.person === undefined && edge !== null && edge.page === page
      ? isPageReady
        ? (edge.which === 'first' ? rows[0] : rows.at(-1))?.id
        : edge.from
      : undefined;
  const openPerson = search.person ?? edgePerson;
  const openIndex = openPerson === undefined ? -1 : ids.indexOf(openPerson);

  const open = (personId: string): void => {
    setEdge(null);
    go({ ...search, invite: undefined, person: personId });
  };
  const close = (): void => {
    setEdge(null);
    go({ ...search, invite: undefined, person: undefined });
  };
  const openInvite = (): void => {
    setEdge(null);
    go({ ...search, person: undefined, invite: true });
  };

  const move = (step: 1 | -1): (() => void) | null => {
    if (openIndex < 0) return rows.length > 0 ? () => open(ids[0]) : null;
    const next = openIndex + step;
    if (next >= 0 && next < rows.length) return () => open(ids[next]);
    const from = ids[openIndex];
    if (step === 1 && page < pageCount) {
      return () => {
        setEdge({ page: page + 1, which: 'first', from });
        go({ ...search, page: page + 1, person: undefined });
      };
    }
    if (step === -1 && page > 1) {
      return () => {
        setEdge({ page: page - 1, which: 'last', from });
        go({ ...search, page: page - 1 > 1 ? page - 1 : undefined, person: undefined });
      };
    }
    return null;
  };

  const navigation: PaneNavigation = {
    position: openIndex >= 0 && total !== undefined ? { index: (page - 1) * PEOPLE_PAGE_SIZE + openIndex + 1, total } : null,
    onPrevious: move(-1),
    onNext: move(1),
  };

  /**
   * 06-qol §2: the arrow keys walk the list while a person is open — not while typing, nor inside a choice. Caught on
   * the way down (review R29), so the table under the cursor doesn't also move its own focus for the same key.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (openPerson === undefined || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const target = event.target as HTMLElement;
    if (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    // A pop-up's keys are its own: React carries them up from its portal to here.
    if (target.closest('[role="radiogroup"], [role="listbox"], [role="menu"], [data-popover]')) return;
    const step = move(event.key === 'ArrowDown' ? 1 : -1);
    if (!step) return;
    event.preventDefault();
    event.stopPropagation();
    step();
  };

  const filter = filterOf(search);
  const activeFilters = filterCount(search);
  const clearFilters = (): void => {
    setTyped(null);
    window.clearTimeout(typingTimer.current);
    go({ sort: search.sort, person: search.person, invite: search.invite });
  };

  const roleValue = search.roleId ?? (roles.data ?? []).find((role) => role.key === search.roleKey)?.id ?? ANY;

  const columns: readonly Column<UserListRow>[] = [
    {
      id: 'fullName',
      header: 'Name',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 180,
      cell: (row) => (
        <span className={styles.person} data-open={row.id === openPerson || undefined}>
          <Avatar name={row.fullName} size="sm" />
          <span className={styles.personText}>
            <Link
              to="/admin/users/$userId"
              params={{ userId: row.id }}
              className={styles.name}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                // A plain click opens the pane; Ctrl-click and the middle button still open a new tab.
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                open(row.id);
              }}
            >
              {row.fullName}
            </Link>
            <span className={styles.email}>{row.email}</span>
          </span>
        </span>
      ),
      exportValue: (row) => `${row.fullName} · ${row.email}`,
    },
    {
      id: 'roleLabels',
      header: 'Roles and reach',
      isSortable: true,
      defaultWidth: 160,
      cell: (row) => (
        <span className={styles.personText}>
          <span className={styles.roles}>{row.roleLabels === '' ? EMPTY : row.roleLabels}</span>
          <span className={styles.email}>{reachOf(row)}</span>
        </span>
      ),
      exportValue: (row) => `${row.roleLabels === '' ? EMPTY : row.roleLabels} · ${reachOf(row)}`,
    },
    {
      id: 'status',
      header: 'Status',
      isSortable: true,
      defaultWidth: 110,
      cell: (row) => {
        const status = listStatusOf(row);
        return <ToneChip tone={PEOPLE_STATUS_TONES[status]}>{PEOPLE_STATUS_LABELS[status]}</ToneChip>;
      },
      exportValue: (row) => PEOPLE_STATUS_LABELS[listStatusOf(row)],
    },
    {
      id: 'lastLoginAt',
      header: 'Last signed in',
      isSortable: true,
      defaultWidth: 150,
      cell: (row) =>
        row.status === 'invited' && row.inviteExpiresAt !== null
          ? `Invite ends ${formatDateTime(row.inviteExpiresAt)}`
          : row.lastLoginAt === null
            ? null
            : formatDateTime(row.lastLoginAt),
    },
    {
      id: 'isPanelEligible',
      header: 'Panel',
      isSortable: true,
      defaultWidth: 90,
      cell: (row) => (row.isPanelEligible ? 'Yes' : 'No'),
    },
    {
      id: 'isTwoFactorEnabled',
      header: 'Two-factor',
      defaultWidth: 110,
      cell: (row) => (row.isTwoFactorEnabled ? 'Set up' : 'Not set up'),
    },
  ];

  const preferences = useTablePreferences(SCREEN_ID, columns, HIDDEN_AT_FIRST);

  // ADR 0073: People is Admin's; anyone else is told so, after every hook has run.
  if (!canView) return <ForbiddenState resource="managing people" contact="Your admin" />;

  const paneContent =
    search.invite === true && canEdit ? (
      <InvitePane mode={paneMode} invitedWaiting={allCounts.data?.invited} onShowInvited={() => filterBy({ status: 'invited' })} onClose={close} />
    ) : openPerson !== undefined ? (
      <PersonPane key="person" userId={openPerson} canEdit={canEdit} mode={paneMode} navigation={navigation} onClose={close} />
    ) : null;

  // On a phone the pane is a screen of its own.
  if (isPhone && paneContent !== null) return <div className={styles.phonePage}>{paneContent}</div>;

  const pager =
    rows.length > 0 && total !== undefined ? (
      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={PEOPLE_PAGE_SIZE}
        rowCount={rows.length}
        total={total}
        onPage={(next) => go({ ...search, page: next > 1 ? next : undefined })}
      />
    ) : null;

  const emptyState =
    users.isError && !users.data ? (
      <ServerErrorState requestId={users.error.requestId} onRetry={() => void users.refetch()} />
    ) : activeFilters > 0 ? (
      <FilteredEmptyState filterCount={activeFilters} onClearFilters={clearFilters} isInset />
    ) : (
      <EmptyState
        title="Nobody here yet"
        body="Invite a colleague and they appear here as soon as the invite is sent."
        action={
          canEdit ? (
            <Button variant="solid" icon={UserPlus} onClick={openInvite}>
              Invite people
            </Button>
          ) : undefined
        }
        isInset
      />
    );

  // A phone shows people as cards, as the 375 drawing does: a table there would scroll sideways.
  const list = isPhone ? (
    <div className={styles.list}>
      {rows.length === 0 && !users.isPending ? (
        emptyState
      ) : (
        <ul className={styles.cards} aria-label="People">
          {rows.map((row) => {
            const status = listStatusOf(row);
            return (
              <li key={row.id}>
                <button type="button" className={styles.card} onClick={() => open(row.id)}>
                  <Avatar name={row.fullName} size="sm" />
                  <span className={styles.personText}>
                    <span className={styles.name}>{row.fullName}</span>
                    <span className={styles.email}>
                      {row.roleLabels === '' ? EMPTY : row.roleLabels} · {reachOf(row)}
                    </span>
                  </span>
                  {status !== 'active' && <ToneChip tone={PEOPLE_STATUS_TONES[status]}>{PEOPLE_STATUS_LABELS[status]}</ToneChip>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {pager}
    </div>
  ) : (
    <div className={`${styles.list} ${fit.fill}`}>
      <DataTable
        screenId={SCREEN_ID}
        label="People"
        columns={columns}
        rows={rows}
        preferences={preferences}
        getRowId={(row) => row.id}
        total={total}
        sort={sortOf(search)}
        onSortChange={(next: readonly SortSpec[]) => go({ ...search, sort: sortParamOf(next), page: undefined })}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) => open(row.id)}
        linkOf={(row) => `/admin/users/${row.id}`}
        exportName="people"
        isLoading={users.isPending}
        rowActions={(row) =>
          canEdit && row.status === 'invited' && row.pendingInviteId !== null ? (
            <Button
              variant="ghost"
              size="sm"
              icon={RotateCw}
              aria-label={`Send ${row.fullName}'s invite again`}
              title="Send the invite again"
              onClick={() =>
                resend.mutate(row.pendingInviteId as string, {
                  onSuccess: () => showToast({ tone: 'success', message: `Invite sent again to ${row.email}`, detail: 'The previous link stopped working.' }),
                  onError: (error) => showToast({ tone: 'danger', message: error.message }),
                })
              }
            />
          ) : null
        }
        bulkActions={
          canEdit ? (
            <PeopleBulkActions
              selection={selection}
              filter={filter}
              total={total}
              onResult={(title, response) => {
                setBulkResult({ title, response });
                setSelection(EMPTY_SELECTION);
              }}
            />
          ) : undefined
        }
        emptyState={emptyState}
      />
      {pager}
    </div>
  );

  return (
    <div className={`${styles.page} ${fit.fit}`} onKeyDownCapture={onKeyDown}>
      <header className={styles.head}>
        <span className={styles.crumb}>Admin · People and access</span>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>People</h1>
          <span className={styles.total}>{counts.data ? formatNumber(counts.data.all) : EMPTY}</span>
          <span className={styles.grow} />
          {canEdit && (
            <span className={styles.headAction}>
              <Button variant="solid" icon={UserPlus} onClick={openInvite}>
                Invite people
              </Button>
            </span>
          )}
        </div>
        <p className={styles.lead}>
          Everyone who can sign in to Nest Recruit: what they can do, and where. Click a person to see and change
          everything about them.
        </p>
      </header>

      <div className={styles.tiles} role="group" aria-label="Show people who are">
        <StatusTile label="All" count={counts.data?.all} isOn={search.status === undefined} onClick={() => filterBy({ status: undefined })} />
        {USER_LIST_STATUSES.map((status: UserListStatus) => (
          <StatusTile
            key={status}
            label={PEOPLE_STATUS_LABELS[status]}
            tone={PEOPLE_STATUS_TONES[status]}
            count={counts.data?.[status]}
            isOn={search.status === status}
            onClick={() => filterBy({ status })}
          />
        ))}
      </div>

      <div className={styles.toolbar}>
        <span className={styles.search}>
          <SearchInput
            aria-label="Search people"
            placeholder="Search names and emails"
            value={typed ?? search.q ?? ''}
            onChange={(event) => onType(event.target.value)}
            onClear={() => onType('')}
            // `/` focuses the primary search on any list screen (06-qol §2).
            data-primary-search
          />
        </span>
        <span className={styles.filter}>
          <span id={filterIds.role} className="sr-only">
            Filter by role
          </span>
          <Select
            describedBy={filterIds.role}
            label="Role"
            value={roleValue}
            options={[
              { value: ANY, label: 'Any role' },
              ...(roles.data ?? []).filter((role) => role.key !== 'candidate').map((role) => ({ value: role.id, label: role.label })),
            ]}
            onChange={(value) => filterBy({ roleId: value === ANY ? undefined : value, roleKey: undefined })}
          />
        </span>
        <span className={styles.filter}>
          <span id={filterIds.venture} className="sr-only">
            Filter by venture
          </span>
          <Select
            describedBy={filterIds.venture}
            label="Venture"
            value={search.ventureId ?? ANY}
            options={[{ value: ANY, label: 'Any venture' }, ...(ventures.data ?? []).map((venture) => ({ value: venture.id, label: venture.name }))]}
            onChange={(value) => filterBy({ ventureId: value === ANY ? undefined : value })}
          />
        </span>
        <span className={styles.filter}>
          <span id={filterIds.panel} className="sr-only">
            Filter by interview panels
          </span>
          <Select
            describedBy={filterIds.panel}
            label="Interview panels"
            value={search.panel ?? ANY}
            options={[
              { value: ANY, label: 'Panel: any' },
              { value: 'yes', label: 'On interview panels' },
              { value: 'no', label: 'Not on panels' },
            ]}
            onChange={(value) => filterBy({ panel: value === 'yes' || value === 'no' ? value : undefined })}
          />
        </span>
        <span className={styles.filter}>
          <span id={filterIds.twoFactor} className="sr-only">
            Filter by two-factor
          </span>
          <Select
            describedBy={filterIds.twoFactor}
            label="Two-factor"
            value={search.twoFactor ?? ANY}
            options={[
              { value: ANY, label: 'Two-factor: any' },
              { value: 'on', label: 'Two-factor set up' },
              { value: 'off', label: 'Two-factor not set up' },
            ]}
            onChange={(value) => filterBy({ twoFactor: value === 'on' || value === 'off' ? value : undefined })}
          />
        </span>
        {activeFilters > 0 && (
          <button type="button" className={styles.clearAll} onClick={clearFilters}>
            Clear all
          </button>
        )}
      </div>

      {bulkResult && (
        <ResultList
          title={bulkResult.title}
          totals={bulkTotals(bulkResult.response)}
          lines={bulkLines(bulkResult.response)}
          onDismiss={() => setBulkResult(null)}
        />
      )}

      <div className={`${styles.layout} ${fit.fill}`} data-pane={paneContent !== null && paneMode === 'docked' ? 'docked' : undefined}>
        {list}
        {paneContent !== null && paneMode === 'docked' && paneContent}
      </div>

      {/* A phone has the one primary button full width at the bottom (FRAME-SPEC §5). */}
      {canEdit && (
        <div className={styles.footAction}>
          <Button variant="solid" icon={UserPlus} block onClick={openInvite}>
            Invite people
          </Button>
        </div>
      )}

      {paneContent !== null && paneMode === 'overlay' && (
        <>
          <button type="button" className={styles.scrim} aria-label="Close the pane" tabIndex={-1} onClick={close} />
          {paneContent}
        </>
      )}
    </div>
  );
}

function StatusTile({
  label,
  count,
  tone,
  isOn,
  onClick,
}: {
  readonly label: string;
  readonly count: number | undefined;
  readonly tone?: string;
  readonly isOn: boolean;
  readonly onClick: () => void;
}): ReactNode {
  return (
    <button type="button" className={`${toneClass} ${styles.tile}`} data-tone={tone} aria-pressed={isOn} onClick={onClick}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileCount}>{count === undefined ? EMPTY : formatNumber(count)}</span>
    </button>
  );
}

function stripInvite(search: PeopleSearch): Omit<PeopleSearch, 'invite'> & { readonly invite?: true } {
  const { invite, ...rest } = search;
  return invite ? { ...rest, invite: true } : rest;
}
